import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle } from "lucide-react";

// Update Error Toast Component
const ErrorToast = ({ message, onComplete }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.3 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.3 }}
    className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
  >
    <motion.div
      initial={{ y: 50 }}
      animate={{ y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2 }}
        className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4"
      >
        <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
      </motion.div>
      <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
        Error
      </h3>
      <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
      <motion.div
        className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden"
        initial={{ width: "100%" }}
        animate={{ width: "0%" }}
        transition={{ duration: 2 }}
        onAnimationComplete={onComplete}
      />
    </motion.div>
  </motion.div>
);

const AuthenticationForm = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "login";
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    department: "",
    batch: "",
  });

  // Add auto-dismiss functionality
  const handleErrorToast = (message) => {
    setToastMessage(message);
    setShowErrorToast(true);
    // Toast will auto-dismiss after animation completes
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      handleErrorToast("Email is required");
      return false;
    }

    // Email format and domain validation for signup
    if (mode === "signup") {
      const emailRegex = /^[^\s@]+@rknec\.edu$/;
      if (!emailRegex.test(formData.email.trim())) {
        handleErrorToast("Kindly use your college email address");
        return false;
      }
    } else {
      // Regular email validation for login
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        handleErrorToast("Please enter a valid email address");
        return false;
      }
    }

    if (!formData.password.trim()) {
      handleErrorToast("Password is required");
      return false;
    }

    if (formData.password.length < 6) {
      handleErrorToast("Password must be at least 6 characters long");
      return false;
    }

    if (mode === "signup" && formData.password !== formData.confirmPassword) {
      handleErrorToast("Passwords don't match");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      if (mode === "login") {
        await login(formData.email.trim(), formData.password.trim());
        navigate("/profile");
      } else {
        await signup(formData.email.trim(), formData.password.trim(), {
          name: formData.name.trim(),
          department: formData.department.trim(),
          batch: formData.batch.trim(),
        });
        navigate("/auth?mode=login");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <AnimatePresence>
        {showErrorToast && (
          <ErrorToast
            message={toastMessage}
            onComplete={() => setShowErrorToast(false)}
          />
        )}
      </AnimatePresence>

      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 lg:p-8 transition-all duration-300">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
            {mode === "login" ? "Login" : "Sign Up"}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Batch Year
                  </label>
                  <input
                    type="text"
                    name="batch"
                    className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={formData.batch}
                    onChange={(e) =>
                      setFormData({ ...formData, batch: e.target.value })
                    }
                    required
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Email
              </label>
              <input
                type="email"
                name="email"
                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Password
              </label>
              <input
                type="password"
                name="password"
                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
              />
            </div>

            {mode === "signup" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                />
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white p-2 rounded transition-colors duration-200 disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : mode === "login"
                ? "Login"
                : "Sign Up"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() =>
                navigate(`/auth?mode=${mode === "login" ? "signup" : "login"}`)
              }
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {mode === "login"
                ? "Need an account? Sign Up"
                : "Already have an account? Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationForm;
